import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard } from 'react-native';
import { t } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';

import EditProfile from '@src/api/EditProfile';
import { shortTimeout as timeout } from '@src/configuration/constants';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useIsMounted from '@src/hooks/useIsMounted';
import { PickedMedia } from '@src/hooks/useMediaPicker';
import useMediaUploader from '@src/hooks/useMediaUploader';
import { i18n, i18yup } from '@src/i18n';

import { EditProfileFormData } from './types';

const useEditProfile = (media?: PickedMedia) => {
  const {
    tokens: { accessToken, userID },
  } = useAuthenticationTokens();
  const navigation = useNavigation();
  const isMounted = useIsMounted();
  const { upload, progress } = useMediaUploader();

  const form = Form.useForm<EditProfileFormData>({
    validationSchema: i18yup.object().shape<EditProfileFormData>({
      bio: i18yup
        .string()
        .max(120)
        .label(i18n._(t`Bio`)),
      name: i18yup.string().label(i18n._(t`Name`)),
      username: i18yup
        .string()
        .required()
        .matches(
          /^[a-zA-Z0-9_-]{5,20}$/,
          i18n._(t`Username must be alphanumeric`)
        )
        .label(i18n._(t`Username`)),
      coverPhoto: i18yup.string().label(i18n._(t`Cover Photo`)),
      profilePicture: i18yup
        .string()
        .notRequired()
        .label(i18n._(t`Profile Picture`)),
      gender: i18yup
        .string()
        .oneOf(['MALE', 'FEMALE', 'RATHER_NOT_SAY'])
        .label(i18n._(t`Gender`)),
      link: i18yup.string().label(i18n._(t`Link`)),
    }),
  });
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = React.useCallback(
    async (data: EditProfileFormData) => {
      const { name, gender, username, bio, link, profilePicture } = data;
      setLoading(true);

      try {
        Keyboard.dismiss();

        if (!media?.cancelled && media) {
          await upload(media.uri, 'image', async (uploadUrl) => {
            const _profilePicture = uploadUrl.replace(/\S+\/(\S+)/, '$1');
            await EditProfile(
              accessToken,
              {
                gender,
                name,
                username,
                validGender: true,
                bio,
                link,
                profilePicture: _profilePicture,
              },
              { timeout }
            );
            setTimeout(() => {
              navigation.navigate('Profile', { id: userID });
            }, 500);
            if (isMounted) {
              setLoading(false);
            }
          });
        } else {
          await EditProfile(
            accessToken,
            {
              gender,
              name,
              username,
              validGender: true,
              bio,
              link,
              profilePicture,
            },
            { timeout }
          );
          setTimeout(() => {
            navigation.navigate('Profile', { id: userID });
          }, 500);
          if (isMounted) {
            setLoading(false);
          }
        }
      } catch (err) {
        console.log('useEditProfile: ', err);
        if (isMounted) {
          setLoading(false);
        }
      }
    },
    [accessToken, isMounted, media, navigation, upload, userID]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
      uploadProgress: progress,
    }),
    [form, handleSubmit, loading, progress]
  );
};

export default useEditProfile;
